import _ from 'lodash';
import { AGGREGATION_MODES, CAPSULE_MODES, Y_VALUE_BIN_MODES } from '@/tools/histogram/histogram.constants';
import { toNumber } from '@/utilities/numberHelper.utilities';
import { validateBinConfig } from '@/utilities/investigateHelper.utilities';
import { SAMPLE_FROM_SCALARS } from '@/services/calculationRunner.constants';
import type { ToolConfigParams, ToolItem, ToolParameter } from '@/toolSelection/baseTool.store';
import { BaseToolStore } from '@/toolSelection/baseTool.store';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import type { StoredStatistic } from '@/tools/StatisticSelector.molecule';

type AggregationMode = typeof AGGREGATION_MODES[keyof typeof AGGREGATION_MODES];
type CapsuleModeKey = typeof CAPSULE_MODES[number]['key'];
type YValueBinMode = typeof Y_VALUE_BIN_MODES[keyof typeof Y_VALUE_BIN_MODES];

export interface AggregationTimeBucket {
  key: string;
  display?: string;
  funct?: string;
  [key: string]: string | undefined;
}

export type Item = (ToolItem & { id: string; name?: string }) & {
  dataStatus?: string;
  lastFetchRequest?: string;
};

export interface AggregationConfigState {
  id: number;
  mode: AggregationMode;
  capsuleMode: CapsuleModeKey;
  yValueBinMode: YValueBinMode;
  valid: boolean;
  yValueBinMin?: number;
  yValueBinMax?: number;
  binSize?: number;
  numberOfBins?: number;
  item?: Item;
  conditionProperty?: string;
  selectedProperty?: string;
  timeBucket?: AggregationTimeBucket;
}

const DEFAULT_AGGREGATION_CONFIG: AggregationConfigState = {
  id: 1,
  mode: AGGREGATION_MODES.Y_VALUE,
  capsuleMode: CAPSULE_MODES[0].key,
  yValueBinMode: Y_VALUE_BIN_MODES.SIZE,
  valid: false,
};

export interface AggregationStoreConfig {
  stat?: StoredStatistic;
  statistic?: string | null;
  timeUnits?: string;
  aggregationConfigs?: AggregationConfigState[];
}

export class AggregationBinStore extends BaseToolStore {
  type = TREND_TOOLS.AGGREGATION_BINS_TABLE;
  static readonly storeName = 'sqAggregationBinStore';
  parameterDefinitions = {
    signalToAggregate: { predicate: ['name', 'signalToAggregate'] },
  };

  initialize() {
    this.state = this.immutable(
      _.assign({}, this.parameterDefinitions, {
        mode: AGGREGATION_MODES.Y_VALUE,
        includeEmptyBuckets: true,
        yValueSignal: '',
        stat: { key: null, timeUnits: 's' },
        aggregationConfigs: [DEFAULT_AGGREGATION_CONFIG],
      }),
    );
  }

  get signalToAggregate() {
    return this.state.get('signalToAggregate');
  }

  get aggregationConfigs(): AggregationConfigState[] {
    return this.state.get('aggregationConfigs');
  }

  get stat(): StoredStatistic {
    return this.state.get('stat');
  }

  get includeEmptyBuckets(): boolean {
    return this.state.get('includeEmptyBuckets');
  }

  private toStoreItem(item?: ToolItem | Item | null): Item | undefined {
    const id = item?.id;
    if (!id) {
      return undefined;
    }

    const base: Item = {
      id,
      ...(item?.name ? { name: item.name } : {}),
      dataStatus: (item as Item | undefined)?.dataStatus,
      lastFetchRequest: (item as Item | undefined)?.lastFetchRequest,
    };

    const optionalFields = _.pick(item, [
      'itemType',
      'redacted',
      'valueUnitOfMeasure',
      'signalMetadata',
      'conditionMetadata',
      'assets',
    ]) as Partial<Item>;

    return {
      ...base,
      ...optionalFields,
    };
  }

  /**
   * Exports state so it can be used to re-create the state later using `rehydrate`.
   *
   * @return {Object} State for the store
   */
  dehydrate() {
    return this.state.serialize();
  }

  /**
   * Sets the aggregation panel state
   *
   * @param {Object} dehydratedState - Previous state usually obtained from `dehydrate` method.
   */
  rehydrate(dehydratedState: AggregationStoreConfig) {
    this.state.merge(dehydratedState);
  }

  /**
   * Validates the provided aggregation config. Based on the mode property different attributes must be provided for a
   * config to be valid.
   *
   * @param config - Object defining the config.
   * @returns true if the config is considered valid, false otherwise.
   */
  validateAggregationConfig(config: AggregationConfigState): boolean {
    if (config.mode === AGGREGATION_MODES.Y_VALUE) {
      if (
        _.isNil(config.yValueBinMin) ||
        _.isNil(config.yValueBinMax) ||
        _.isNil(config.binSize) ||
        _.isEmpty(config.item)
      ) {
        return false;
      }

      const validationError = validateBinConfig({
        yValueBinMin: config.yValueBinMin,
        yValueBinMax: config.yValueBinMax,
        binSize: config.binSize,
        numberOfBins: config.numberOfBins,
      });

      return validationError === '' && !_.isUndefined(config.yValueBinMode);
    }

    if (config.mode === AGGREGATION_MODES.CONDITION) {
      return !_.isEmpty(config.conditionProperty) && !_.isEmpty(config.item);
    }

    return !_.isEmpty(config.timeBucket);
  }

  /**
   * Maps the parameters to the appropriate aggregation config. Also updates from old statistic key to the new ones
   * defined by SAMPLE_FROM_SCALARS.VALUE_METHODS. NOTE: percentGood used to be supported, but it no longer
   * supported so it will fall through and show an empty statistics selector when edited.
   *
   * Also defaults the capsuleMode for condition-based mode to overlaps.
   *
   * @param  {Object} config - The configuration from UIConfig
   * @param  {Object[]} parameters - The parameters of the formula
   * @return {Object} The modified config
   */
  migrateSavedConfig<TConfig extends ToolConfigParams>(config: TConfig, parameters: ToolParameter[]): TConfig {
    const aggregationConfig = config as TConfig & AggregationStoreConfig;

    if (!_.some(SAMPLE_FROM_SCALARS.VALUE_METHODS, ['key', aggregationConfig.statistic])) {
      const statistic = aggregationConfig.statistic;
      aggregationConfig.statistic =
        statistic === 'StdDev' ? 'standardDeviation' : statistic ? _.toLower(statistic) : statistic ?? null;
    }

    _.forEach(aggregationConfig.aggregationConfigs, (aggregationConfigEntry, i) => {
      if (
        aggregationConfig.aggregationConfigs?.[i]?.mode === AGGREGATION_MODES.CONDITION ||
        aggregationConfig.aggregationConfigs?.[i]?.mode === AGGREGATION_MODES.TIME
      ) {
        if (_.isUndefined(aggregationConfig.aggregationConfigs?.[i]?.capsuleMode)) {
          aggregationConfig.aggregationConfigs![i]!.capsuleMode = DEFAULT_AGGREGATION_CONFIG.capsuleMode;
        }
      }

      if (_.has(aggregationConfigEntry, 'selectedProperty')) {
        if (aggregationConfig.aggregationConfigs) {
          aggregationConfig.aggregationConfigs[i]!.conditionProperty = aggregationConfigEntry.selectedProperty;
          delete aggregationConfig.aggregationConfigs[i]!.selectedProperty;
        }
      }

      const parameterItem = _.chain(parameters)
        .find(
          (parameter) =>
            toNumber(String(parameter.name ?? '').replace(/.*?(\d+)$/, '$1')) === aggregationConfigEntry.id,
        )
        .get('item')
        .value() as ToolItem | undefined;

      const storeItem = this.toStoreItem(parameterItem);
      if (storeItem && aggregationConfig.aggregationConfigs) {
        aggregationConfig.aggregationConfigs[i]!.item = storeItem;
      }
    });
    if (_.isUndefined((aggregationConfig as AggregationStoreConfig).stat)) {
      aggregationConfig.stat = {
        key: aggregationConfig.statistic ?? null,
        timeUnits: aggregationConfig.timeUnits ?? 's',
      };
      delete aggregationConfig.statistic;
      delete aggregationConfig.timeUnits;
    }
    return config;
  }

  /**
   * Removes the item from aggregationConfigs since the source of truth is the formula parameters.
   *
   * @param {Object} config - The state that will be saved to UIConfig
   * @return {Object} The modified config
   */
  modifyConfigParams<TConfig extends ToolConfigParams>(config: TConfig): TConfig {
    const aggregationConfig = config as TConfig & AggregationStoreConfig;
    aggregationConfig.aggregationConfigs = aggregationConfig.aggregationConfigs?.map(({ item, ...rest }) => rest);
    return config;
  }

  protected readonly handlers = {
    ...this.baseHandlers,

    /**
     * Sets the statistic that is used to create the aggregation.
     *
     * @param {Object} payload - Object container
     * @param {Object} payload.stat - the statistic from the selector
     */
    AGGREGATION_SET_STAT: (payload: { stat: { key: string; timeUnits: string } }) => {
      this.state.set('stat', payload.stat);
    },

    /**
     * This function finds the aggregationConfig entry that is being modified and updates the parameters specified.
     * If no entry for the provided id is found a new one is added.
     * To enable form-validation it also validates the entry and sets the "valid" flag to true if the definition is
     * valid.
     *
     * @param {Object} payload -  Object container
     * @param {Number} payload.id -  id defining the aggregationConfig being modified.
     * @param {String} [payload.mode] -  one of AGGREGATION_MODES. Applies to all aggregationConfig entries.
     * @param {Number} [payload.yValueBinMin] -  minimum y-value to be used for creating bins by y-value. Only
     *   applies to AGGREGATION_MODES.Y_VALUE
     * @param {Number} [payload.yValueBinMax] -  maximum y-value to be used for creating bins by y-value. Only
     *   applies to AGGREGATION_MODES.Y_VALUE
     * @param {Number} [payload.binSize] - size of the bin. Only applies to AGGREGATION_MODES.Y_VALUE
     * @param {Number} [payload.numberOfBins] - number of bins. Only applies to AGGREGATION_MODES.Y_VALUE
     * @param {String} [payload.yValueBinMode] - whether to create bins based on size or a certain number. Only
     *   applies to AGGREGATION_MODES.Y_VALUE
     * @param {String} [payload.conditionProperty] - the condition property. Only applies to AGGREGATION_MODES.CONDITION
     * @param {Number} [payload.capsuleMode] - Boundary selection of capsules. Only applies to
     *   AGGREGATION_MODES.CONDITION or AGGREGATION_MODES.TIME
     * @param {Object} [payload.item] - the item used as part of the aggregation. Does not apply to
     *   AGGREGATION_MODES.TIME.
     * @param {Object} [payload.timeBucket] - the time bucket definition. Only applies to AGGREGATION_MODES.TIME
     */
    AGGREGATION_SET_VALUE_PARAMS: (payload: Partial<AggregationConfigState> & { id: number; item?: Item | null }) => {
      const index = _.findIndex(this.state.get('aggregationConfigs'), {
        id: payload.id,
      });
      if (index > -1) {
        const originalConfig = this.state.get(['aggregationConfigs', index]) as AggregationConfigState;
        const config: AggregationConfigState = _.assign({}, originalConfig, _.omit(payload, ['item']));
        if (_.has(payload, 'item')) {
          config.item = this.toStoreItem(payload.item ?? undefined);
        } else if (originalConfig?.mode !== config.mode) {
          delete config.item;
        }

        config.valid = this.validateAggregationConfig(config);
        this.state.set(['aggregationConfigs', index], config);
      }
    },

    /**
     * Set the emptyBucketsAllowed flag.
     *
     * @param {Object} payload - Object container
     * @param {Boolean} payload.emptyBucketsAllowed - true if empty buckets should be included in the result
     */
    AGGREGATION_SET_ALLOW_EMPTY_BUCKETS: (payload: { emptyBucketsAllowed: boolean }) => {
      this.state.set('includeEmptyBuckets', payload.emptyBucketsAllowed);
    },

    /**
     * Adds an entry to the aggregationConfigs array. This function also assigns a unique id to the entry that can
     * then be used to update it.
     */
    AGGREGATION_ADD_AGGREGATION: () => {
      const configs: AggregationConfigState[] = this.state.get('aggregationConfigs') ?? [];
      const maxId = (_.max(_.map(configs, (cfg) => cfg.id)) as number) ?? 0;
      const id = maxId + 1;
      this.state.push('aggregationConfigs', _.assign({}, DEFAULT_AGGREGATION_CONFIG, { id }));
      // Empty buckets cannot be included once there is more than one grouping
      this.state.set('includeEmptyBuckets', false);
    },

    /**
     * Removes an entry from the aggregationConfigs.
     *
     * @param {Object} payload - Object container
     * @param {String} payload.id - id of the aggregation to remove.
     */
    AGGREGATION_REMOVE: (payload: { id: string }) => {
      const index = _.findIndex(this.state.get('aggregationConfigs'), {
        id: payload.id,
      });

      if (index > -1) {
        this.state.splice('aggregationConfigs', [index, 1]);
      }

      if (this.state.get('aggregationConfigs').length === 1) {
        this.state.set('includeEmptyBuckets', true);
      }
    },
  };
}
